import { useEffect, useState } from 'react';

import { IconX } from './icons';
import { ALL_CATEGORIES, CATEGORY_LABELS } from './incidentLabels';
import { useToast } from '../../contexts/ToastContext';
import { hasBackError, toUserErrorMessage } from '../../lib/apiError';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { useCreateIncident } from '../../services/api/hooks/useCreateIncident';
import { useRides } from '../../services/api/hooks/useRides';
import type { IncidentCategory } from '../../types/extranet-api.types';
import { useFilterStore } from '../filters/filterStore';
import { getCity, getExternalRef } from '../rides/trip';

interface CreateIncidentModalProps {
  /** Course pré-remplie, quand la modale est ouverte depuis la table des courses. */
  initialCourseId?: string;
  onClose: () => void;
}

const MIN_REPORT = 10;
const MAX_REPORT = 5000;

/**
 * `POST /v1/incidents` — la course est identifiée par `courseId`, qui accepte
 * l'id interne (`R…`) **ou** la référence externe SNCF. Le champ est donc une
 * saisie libre ; la recherche par nom de passager sert d'aide au remplissage.
 */
export const CreateIncidentModal = ({ initialCourseId, onClose }: CreateIncidentModalProps) => {
  const toast = useToast();
  const { scopes, dateRange } = useFilterStore();

  const [courseId, setCourseId] = useState(initialCourseId ?? '');
  const [clientReport, setClientReport] = useState('');
  const [category, setCategory] = useState<IncidentCategory | ''>('');
  const [touched, setTouched] = useState(false);

  const mutation = useCreateIncident();

  // La saisie sert à la fois de `courseId` et de recherche par nom de passager :
  // l'utilisateur peut coller un identifiant ou chercher un nom.
  const passengerLastName = useDebouncedValue(courseId.trim());
  const ridesQuery = useRides({
    scopes,
    dateRange,
    page: 1,
    limit: 6,
    passengerLastName: passengerLastName || undefined,
  });
  const suggestions = passengerLastName.length >= 2 ? (ridesQuery.data?.items ?? []) : [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const reportError =
    clientReport.length > 0 && (clientReport.length < MIN_REPORT || clientReport.length > MAX_REPORT)
      ? `Remontée client : ${MIN_REPORT} à ${MAX_REPORT} caractères (actuel : ${clientReport.length})`
      : null;

  const courseNotFound = hasBackError(mutation.error, 'COURSE_NOT_FOUND');

  const canSubmit =
    Boolean(courseId.trim()) &&
    Boolean(category) &&
    clientReport.length >= MIN_REPORT &&
    clientReport.length <= MAX_REPORT &&
    !mutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    mutation.mutate(
      {
        courseId: courseId.trim(),
        category: category as IncidentCategory,
        clientReport: clientReport.trim(),
      },
      {
        onSuccess: created => {
          toast.success('Réclamation enregistrée', `Rattachée à la course ${created.tripKey}.`);
          onClose();
        },
        onError: err => {
          // Le 422 COURSE_NOT_FOUND s'affiche sur le champ concerné, pas en toast.
          if (hasBackError(err, 'COURSE_NOT_FOUND')) return;
          const { title, description } = toUserErrorMessage(err, 'Échec de la création');
          toast.error(title, description);
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      <div
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-incident-title"
        className="relative w-full max-w-[520px] bg-white border border-zinc-200 rounded-md shadow-md drawer-panel-in flex flex-col max-h-[80vh]"
      >
        <header className="sticky top-0 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2 id="create-incident-title" className="text-base font-semibold text-zinc-900">
            Nouvelle réclamation
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
          >
            <IconX width={16} height={16} />
          </button>
        </header>

        <form
          id="create-incident-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="incident-course" className="text-xs font-medium text-zinc-700">
              Course
            </label>
            <input
              id="incident-course"
              type="text"
              value={courseId}
              onChange={e => setCourseId(e.target.value)}
              placeholder="N° de course, référence SNCF, ou nom du passager"
              aria-invalid={courseNotFound || undefined}
              className={[
                'h-10 w-full px-3 text-sm bg-white border rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 transition-colors',
                courseNotFound
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                  : 'border-zinc-300 focus:border-zinc-400 focus:ring-zinc-900/10',
              ].join(' ')}
            />
            {suggestions.length > 0 ? (
              <ul className="border border-zinc-200 rounded-md max-h-44 overflow-y-auto bg-white">
                {suggestions.map(trip => (
                  <li key={trip.id}>
                    <button
                      type="button"
                      onClick={() => setCourseId(trip.id)}
                      className="w-full px-3 py-2 flex items-center gap-3 text-left text-sm text-zinc-700 hover:bg-zinc-50 transition-colors"
                    >
                      <span className="font-mono tabular-nums text-xs text-zinc-900">{trip.id.slice(-12)}</span>
                      <span className="text-zinc-300">·</span>
                      <span className="font-mono tabular-nums text-xs text-zinc-500">{getExternalRef(trip)}</span>
                      <span className="ml-auto text-xs text-zinc-500 truncate max-w-[180px]">{getCity(trip)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {courseNotFound ? (
              <p className="text-xs text-red-600">
                Aucune course ne correspond à cet identifiant sur votre périmètre. Vérifiez le numéro ou la référence.
              </p>
            ) : touched && !courseId.trim() ? (
              <p className="text-xs text-red-600">Une course doit être renseignée.</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="incident-category" className="text-xs font-medium text-zinc-700">
              Type d&apos;incident
            </label>
            <select
              id="incident-category"
              value={category}
              onChange={e => setCategory(e.target.value as IncidentCategory | '')}
              className="h-10 px-2 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors"
            >
              <option value="">— Choisir —</option>
              {ALL_CATEGORIES.map(c => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            {touched && !category ? <p className="text-xs text-red-600">Un type d&apos;incident est requis.</p> : null}
            <p className="text-[11px] text-zinc-500">La priorité est déterminée par Allocab d&apos;après ce type.</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <label htmlFor="incident-report" className="text-xs font-medium text-zinc-700">
                Remontée client
              </label>
              <span
                className={['text-xs font-mono tabular-nums', reportError ? 'text-red-600' : 'text-zinc-500'].join(' ')}
              >
                {clientReport.length} / {MAX_REPORT}
              </span>
            </div>
            <textarea
              id="incident-report"
              value={clientReport}
              onChange={e => setClientReport(e.target.value)}
              rows={4}
              placeholder="Verbatim ou résumé de la réclamation transmise par le client."
              className="w-full px-3 py-2 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors resize-y"
            />
            {reportError ? <p className="text-xs text-red-600">{reportError}</p> : null}
          </div>
        </form>

        <footer className="px-6 py-3 border-t border-zinc-100 flex items-center justify-end gap-2 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="create-incident-form"
            disabled={!canSubmit}
            className="h-9 px-4 text-sm font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending ? 'Création…' : 'Confirmer'}
          </button>
        </footer>
      </div>
    </div>
  );
};
